import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { useTracking } from '../hooks/useTracking';
import { TRACKING_EVENTS } from '../utils/trackingEvents';
import { EntityHistory } from './Analytics';

const GROUP_TYPES = [
  { value: 'prestation', label: 'Prestation', description: 'Groupe de travail pour prestations clients' },
  { value: 'interne', label: 'Interne', description: 'Événement interne (CSE, formation, visite médicale...)' }
];

const GroupForm = ({ token, group, onClose, onSave }) => {
  const { trackAction, trackView } = useTracking();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    group_type: 'prestation',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        code: group.code || '',
        group_type: group.group_type || 'prestation',
        is_active: group.is_active !== false
      });

      // Tracker la consultation (audit)
      trackView(TRACKING_EVENTS.GROUP_VIEWED, 'group', group.id, group.name);
    }
  }, [group, trackView]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = group
        ? API_ENDPOINTS.GROUP_BY_ID(group.id)
        : API_ENDPOINTS.GROUPS;
      const method = group ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          code: formData.code,
          group_type: formData.group_type,
          is_active: formData.is_active
        })
      });

      const data = await response.json();

      if (data.success) {
        // Tracking
        trackAction(
          group ? TRACKING_EVENTS.GROUP_UPDATED : TRACKING_EVENTS.GROUP_CREATED,
          'group',
          null,
          { groupId: data.data?.id, name: formData.name, code: formData.code }
        );
        onSave();
      } else {
        setError(data.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '30px',
        width: '95%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
            {group ? '✏️ Modifier le groupe' : '➕ Nouveau groupe'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            background: '#f8d7da',
            color: '#721c24',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              Nom du groupe <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Équipe A, Véhicule 1..."
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              Code (lettre/identifiant) <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="Ex: A, B, V1..."
              required
              maxLength={10}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e5e9',
                borderRadius: '8px',
                fontSize: '14px',
                textTransform: 'uppercase',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
              Identifiant court affiché dans le planning
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              Type de groupe
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {GROUP_TYPES.map(type => (
                <label
                  key={type.value}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px',
                    border: `2px solid ${formData.group_type === type.value ? (type.value === 'interne' ? '#e65100' : '#6f42c1') : '#e1e5e9'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.group_type === type.value ? (type.value === 'interne' ? '#fff3e0' : '#f3e5f5') : 'white',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      name="group_type"
                      value={type.value}
                      checked={formData.group_type === type.value}
                      onChange={handleChange}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>{type.label}</span>
                  </div>
                  <span style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px', marginLeft: '24px' }}>
                    {type.description}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: '600', fontSize: '14px' }}>Groupe actif</span>
            </label>
          </div>

          {/* Section Historique (mode edition uniquement) */}
          {group?.id && (
            <div style={{ marginBottom: '20px', borderTop: '1px solid #e0e0e0', paddingTop: '15px' }}>
              <button
                type="button"
                onClick={() => setShowHistory(!showHistory)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: '#1976d2',
                  fontWeight: 500
                }}
              >
                <span>{showHistory ? '▼' : '▶'}</span>
                <span>📜 Historique des modifications</span>
              </button>
              {showHistory && (
                <div style={{ marginTop: '12px' }}>
                  <EntityHistory
                    entityType="group"
                    entityId={group.id}
                    token={token}
                    limit={20}
                    showHeader={false}
                    compact={true}
                  />
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                borderRadius: '8px',
                background: '#6c757d',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                borderRadius: '8px',
                background: loading ? '#ccc' : '#28a745',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {loading ? 'Enregistrement...' : (group ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GroupForm;
